import { useCallback, useMemo } from 'react';
import { atom, useAtom } from 'jotai';
import {
    CHAT_BOX_CLOSED_WIDTH_REM,
    CHAT_BOX_OPEN_WIDTH_PX,
    DRAWER_MARGIN_REM,
    DRAWER_WIDTH_REM,
    getChatBoxMinimisedWidthRem,
    SPACING_PX,
} from '../utils';
import { useConversationCache } from './use-conversation-cache';

type ChatInboxState = 'open' | 'closed' | 'hidden';

interface ChatBoxState {
    /**
     * The conversation ID. This is `0` for the new message chat box.
     */
    id: number;
    /**
     * Whether the chat box is open or minimised.
     */
    isOpen: boolean;
    /**
     * The width of the chat box in rem when it is minimised. This is only used
     * to calculate the available space for new chat boxes.
     */
    minimisedWidthRem?: number;
}

const MAX_CHAT_BOXES = 5;

/**
 * The chat inbox drawer state.
 * - open: The chat inbox drawer is open.
 * - closed: The chat inbox drawer is closed but the button is visible.
 * - hidden: The chat inbox drawer button and chat boxes are hidden.
 */
const chatInboxStateAtom = atom<ChatInboxState>('closed');
/**
 * The list of chat boxes and their open/minimised states. This is ordered from
 * the oldest opened to the newest opened.
 */
const chatBoxStatesAtom = atom<ChatBoxState[]>([]);
/**
 * The pending message to be sent when the user sends a message in a new message
 * chat box. When the chat box with this conversation is opened, the message will
 * be sent automatically.
 */
const pendingMessageAtom = atom<{
    conversationID: number;
    message: string;
    attachments: File[];
} | null>(null);

/**
 * Preselected recipient IDs for the new message chat box. When the new message
 * chat box (conversationID=0) is opened, these recipients will be automatically
 * added to the recipients list.
 */
export const preselectedRecipientsAtom = atom<number[]>([]);

/**
 * Hook to handle the website chat functionality. This includes the chat inbox
 * drawer state, and the chat box states. This hook can:
 * - Open and close the chat inbox drawer
 * - Open, close, and minimise chat boxes.
 * - Convert a new message chat box to an existing conversation chat box.
 */
export function useChat() {
    const { getCachedConversation } = useConversationCache();
    const [chatInboxState, setChatInboxState] = useAtom(chatInboxStateAtom);
    const [chatBoxStates, setChatBoxStates] = useAtom(chatBoxStatesAtom);
    const [pendingMessage, setPendingMessage] = useAtom(pendingMessageAtom);
    const isInboxOrChatBoxOpen = useMemo(() => {
        return chatInboxState === 'open' || chatBoxStates.some((c) => c.isOpen);
    }, [chatInboxState, chatBoxStates]);

    const isChatBoxOpen = useCallback(
        (conversationID: number): boolean => {
            return chatBoxStates.some(
                (c) => c.id === conversationID && c.isOpen
            );
        },
        [chatBoxStates]
    );

    const openChatBox = useCallback(
        (conversationID: number) => {
            setChatBoxStates((prev) => {
                const currentState = prev.find((c) => c.id === conversationID);
                // If the chat box is already open, do nothing.
                if (currentState?.isOpen) {
                    return prev;
                }
                const minimisedWidthRem = getChatBoxMinimisedWidthRem(
                    getCachedConversation(conversationID)
                );
                let newState = minimiseOrCloseOtherChatBoxes(
                    prev.map((c) => ({
                        ...c,
                        minimisedWidthRem: getChatBoxMinimisedWidthRem(
                            getCachedConversation(c.id)
                        ),
                    })),
                    conversationID,
                    !!currentState,
                    minimisedWidthRem
                );
                // If the chat box is minimised, open it.
                if (currentState) {
                    return newState.map((c) =>
                        c.id === conversationID ? { ...c, isOpen: true } : c
                    );
                }
                // If the chat box is not open, open it.
                newState = [...newState, { id: conversationID, isOpen: true }];
                // Limit the maximum number of chat boxes.
                if (newState.length > MAX_CHAT_BOXES) {
                    newState.shift();
                }
                return newState;
            });
        },
        [setChatBoxStates, getCachedConversation]
    );

    const openMinimisedChatBox = useCallback(
        (conversationID: number) => {
            setChatBoxStates((prev) => {
                if (
                    prev.some((c) => c.id === conversationID) ||
                    prev.length >= MAX_CHAT_BOXES ||
                    calculateAvailableSpace(prev) <
                        getChatBoxRequiredSpace(false)
                ) {
                    return prev;
                }
                return [...prev, { id: conversationID, isOpen: false }];
            });
        },
        [setChatBoxStates]
    );

    const hideInboxAndChatBoxes = useCallback(() => {
        setChatInboxState('closed');
        setChatBoxStates((prev) => prev.map((c) => ({ ...c, isOpen: false })));
    }, [setChatInboxState, setChatBoxStates]);

    const convertNewMessageToConversation = useCallback(
        (conversationID: number) => {
            if (conversationID <= 0) return;
            setChatBoxStates((prev) => {
                return prev
                    .filter((c) => c.id !== conversationID)
                    .map((c) =>
                        c.id === 0 ? { id: conversationID, isOpen: true } : c
                    );
            });
        },
        [setChatBoxStates]
    );

    return {
        /**
         * The chat inbox drawer state.
         */
        chatInboxState,
        /**
         * The list of chat boxes and their open/minimised states.
         */
        chatBoxStates,
        /**
         * Whether the chat inbox drawer or any chat box is open.
         */
        isInboxOrChatBoxOpen,
        /**
         * The pending message to be sent when the chat box with this
         * conversation is opened. This is set when the user sends a message in
         * the new message chat box in an existing conversation.
         */
        pendingMessage,
        /**
         * Set the chat inbox drawer state.
         */
        setChatInboxState,
        /**
         * Check if the chat box for a conversation is open.
         */
        isChatBoxOpen,
        /**
         * Open the chat box for a conversation. If there is no available screen
         * space, the oldest chat box(es) will be minimised or closed. Pass `0`
         * as the conversation ID to create a new conversation.
         */
        openChatBox,
        /**
         * Open a chat box in the minimised state it is not opened/minimised
         * already and there is space available. If the maximum number of chat
         * boxes is reached or there is no available space, do nothing.
         */
        openMinimisedChatBox,
        /**
         * Minimize the chat box for a conversation.
         */
        minimizeChatBox: useCallback(
            (conversationID: number) => {
                setChatBoxStates((prev) => {
                    return prev.map((c) =>
                        c.id === conversationID ? { ...c, isOpen: false } : c
                    );
                });
            },
            [setChatBoxStates]
        ),
        /**
         * Close the chat box for a conversation.
         */
        closeChatBox: useCallback(
            (conversationID: number) => {
                setChatBoxStates((prev) => {
                    return prev.filter((c) => c.id !== conversationID);
                });
            },
            [setChatBoxStates]
        ),
        /**
         * Close the inbox drawer and minimise all of the chat boxes.
         */
        hideInboxAndChatBoxes,
        /**
         * Set the pending message to be sent when the chat box with this
         * conversation is opened. Call this when the user sends a message in
         * the new message chat box in an existing conversation.
         */
        setPendingMessage,
        /**
         * Convert the new message chat box to a conversation chat box. If the
         * new message chat box is not open, this will do nothing except close
         * the conversation chat box it exists.
         */
        convertNewMessageToConversation,
    };
}

/**
 * Minimise or close other chat boxes to make space for the new chat box to open.
 * @param chatBoxes The list of chat boxes.
 * @param conversationID The ID of the conversation to be opened.
 * @param isMinimised The chat box to open is currently minimised, instead of closed.
 * @param minimisedWidthRem The width of the chat box to open in rem when it is minimised.
 * @returns The list of chat boxes after minimising or closing other chat boxes.
 */
function minimiseOrCloseOtherChatBoxes(
    chatBoxes: ChatBoxState[],
    conversationID: number,
    isMinimised: boolean,
    minimisedWidthRem: number = CHAT_BOX_CLOSED_WIDTH_REM
): ChatBoxState[] {
    const requiredSpace = getChatBoxRequiredSpace(
        isMinimised,
        minimisedWidthRem
    );
    let newState = [...chatBoxes];
    // First, minimise open chat boxes one by one until there's enough space.
    let availableSpace = calculateAvailableSpace(newState);
    while (availableSpace < requiredSpace) {
        const indexToMinimise = newState.findIndex(
            (c) => c.isOpen && c.id !== conversationID
        );
        if (indexToMinimise === -1) {
            // No more open chat boxes to minimise.
            break;
        }
        // Minimise this chat box.
        newState[indexToMinimise] = {
            ...newState[indexToMinimise]!,
            isOpen: false,
        };
        availableSpace = calculateAvailableSpace(newState);
    }

    // If there's still not enough space, close chat boxes one by one.
    while (availableSpace < requiredSpace) {
        const indexToClose = newState.findIndex((c) => c.id !== conversationID);
        if (indexToClose === -1) {
            // No more chat boxes to close.
            break;
        }
        // Close this chat box.
        newState.splice(indexToClose, 1);
        availableSpace = calculateAvailableSpace(newState);
    }

    return newState;
}

/**
 * Calculate the available space for a new chat box. This is calculated by
 * taking the window width and subtracting the width of the other chat boxes.
 * @param chatBoxes The list of chat boxes.
 * @returns The available space for new chat boxes in pixels. The value is
 *  clamped between 0 and 1920.
 */
function calculateAvailableSpace(chatBoxes: ChatBoxState[]): number {
    const windowWidth = window.visualViewport?.width || window.innerWidth;
    const rootFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize
    );
    const drawerWidthPx = (DRAWER_WIDTH_REM + DRAWER_MARGIN_REM) * rootFontSize;

    const chatBoxesWidthPx = chatBoxes.reduce((acc, chatBox) => {
        return (
            acc +
            (chatBox.isOpen
                ? CHAT_BOX_OPEN_WIDTH_PX
                : (chatBox.minimisedWidthRem ?? CHAT_BOX_CLOSED_WIDTH_REM) *
                  rootFontSize) +
            SPACING_PX
        );
    }, 0);

    return Math.min(
        Math.max(windowWidth - drawerWidthPx - chatBoxesWidthPx, 0),
        1920
    );
}

/**
 * Get the required space for a chat box in pixels.
 * @param isMinimised Whether the chat box is minimised.
 * @param minimisedWidthRem The width of the chat box in rem when it is minimised.
 *  Group chat chat boxes have a larger minimised width than 1-on-1 chat boxes.
 * @returns The required space for a chat box in pixels.
 */
function getChatBoxRequiredSpace(
    isMinimised: boolean,
    minimisedWidthRem: number = CHAT_BOX_CLOSED_WIDTH_REM
): number {
    if (!isMinimised) {
        return CHAT_BOX_OPEN_WIDTH_PX + SPACING_PX;
    }
    const rootFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize
    );
    return CHAT_BOX_OPEN_WIDTH_PX - minimisedWidthRem * rootFontSize;
}
